// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// The voice-intake orb: one calm luminous sphere whose colour and motion cadence encode
// the live conversation state. Purely presentational and decorative (aria-hidden) — the
// state is announced by a visually-hidden live region in VoiceModeOverlay. When a thinking
// turn runs unusually long (upstream latency), a second slow ring gently fades in so the
// wait still reads as "working" — honest reinforcement, not fabricated progress. Every
// continuous animation is removed under prefers-reduced-motion (see index.css); the sphere
// then reads by colour, size, and opacity alone.

import { useEffect, useState } from 'react'

export type OrbState = 'connecting' | 'listening' | 'thinking' | 'speaking' | 'muted' | 'error' | 'ended'

interface OrbVisual {
  /** Core sphere radial gradient. */
  core: string
  /** Soft ambient halo behind the sphere. */
  aura: string
  /** Animation utility class on the core (see index.css). */
  motion: string
  /** Resting scale — also the static value under reduced motion. */
  scale: number
  /** Resting opacity — dimmer states read as quieter. */
  opacity: number
  /** Show the slow orbiting ring (contemplative cadence). */
  ring: boolean
  /** Emit concentric rings (assistant voice). */
  emanate: boolean
}

const ACTIVE_CORE = 'radial-gradient(circle at 32% 28%, #7FC0FF, #006EF9 52%, #0A4FB8)'
const ACTIVE_AURA = 'rgba(0,110,249,0.34)'

const VISUALS: Record<OrbState, OrbVisual> = {
  connecting: {
    core: ACTIVE_CORE,
    aura: ACTIVE_AURA,
    motion: 'qh-orb-connect',
    scale: 0.9,
    opacity: 0.7,
    ring: false,
    emanate: false,
  },
  listening: {
    core: ACTIVE_CORE,
    aura: ACTIVE_AURA,
    motion: 'qh-orb-breathe',
    scale: 1,
    opacity: 1,
    ring: false,
    emanate: false,
  },
  thinking: {
    core: ACTIVE_CORE,
    aura: ACTIVE_AURA,
    motion: 'qh-orb-drift',
    scale: 0.94,
    opacity: 0.92,
    ring: true,
    emanate: false,
  },
  speaking: {
    core: ACTIVE_CORE,
    aura: ACTIVE_AURA,
    motion: 'qh-orb-speak',
    scale: 1.02,
    opacity: 1,
    ring: false,
    emanate: true,
  },
  muted: {
    core: 'radial-gradient(circle at 32% 28%, #93A3B8, #64748B 52%, #3E4A5C)',
    aura: 'rgba(100,116,139,0.2)',
    motion: '',
    scale: 0.94,
    opacity: 0.7,
    ring: false,
    emanate: false,
  },
  error: {
    core: 'radial-gradient(circle at 32% 28%, #F6C667, #E0940A 52%, #A9690A)',
    aura: 'rgba(224,148,10,0.26)',
    motion: '',
    scale: 0.94,
    opacity: 0.85,
    ring: false,
    emanate: false,
  },
  ended: {
    core: 'radial-gradient(circle at 32% 28%, #3A5A78, #24405A 55%, #16293B)',
    aura: 'rgba(58,90,120,0.18)',
    motion: 'qh-orb-settle',
    scale: 0.7,
    opacity: 0.55,
    ring: false,
    emanate: false,
  },
}

const LONG_THINKING_MS = 6000

export function VoiceOrb({ state }: { state: OrbState }) {
  const v = VISUALS[state]

  // Reinforce a slow reply after a threshold so it never reads as frozen. Reset the moment
  // we leave the thinking state, so a normal-paced turn never triggers the extra ring.
  const [longThinking, setLongThinking] = useState(false)
  useEffect(() => {
    if (state !== 'thinking') {
      setLongThinking(false)
      return
    }
    const id = window.setTimeout(() => setLongThinking(true), LONG_THINKING_MS)
    return () => window.clearTimeout(id)
  }, [state])

  return (
    <div className="qh-orb relative flex items-center justify-center" style={{ width: 176, height: 176 }} aria-hidden>
      {/* Ambient halo */}
      <div
        className="absolute rounded-full"
        style={{ width: 176, height: 176, background: v.aura, filter: 'blur(38px)', opacity: v.opacity }}
      />

      {/* Concentric emanations while the assistant speaks */}
      {v.emanate && (
        <>
          <span className="qh-orb-wave absolute rounded-full" style={{ width: 132, height: 132, border: '1.5px solid rgba(127,192,255,0.5)' }} />
          <span className="qh-orb-wave qh-orb-wave-2 absolute rounded-full" style={{ width: 132, height: 132, border: '1.5px solid rgba(127,192,255,0.35)' }} />
        </>
      )}

      {/* Slow orbiting ring while the assistant is thinking */}
      {v.ring && (
        <span
          className="qh-orb-ring absolute rounded-full"
          style={{ width: 148, height: 148, borderStyle: 'solid', borderWidth: '2px 2px 2px 2px', borderColor: 'rgba(127,192,255,0.65) transparent transparent transparent' }}
        />
      )}

      {/* A slow reply is still working: a second, wider ring fades in and drifts the other way */}
      {v.ring && longThinking && (
        <span
          className="qh-orb-ring-slow absolute rounded-full"
          style={{ width: 168, height: 168, borderStyle: 'solid', borderWidth: '2px 2px 2px 2px', borderColor: 'transparent transparent rgba(127,192,255,0.4) transparent' }}
        />
      )}

      {/* Core sphere */}
      <div
        data-testid="voice-orb-core"
        className={v.motion}
        style={{
          width: 116,
          height: 116,
          borderRadius: '9999px',
          background: v.core,
          opacity: v.opacity,
          transform: `scale(${v.scale})`,
          transition: 'transform 0.6s cubic-bezier(0.22,1,0.36,1), opacity 0.6s ease, background 0.6s ease',
          boxShadow: `0 0 44px 6px ${v.aura}, inset 0 -10px 24px rgba(0,0,0,0.28), inset 0 8px 18px rgba(255,255,255,0.22)`,
        }}
      />

      {/* Muted glyph — a clear break in the sphere so "paused" reads without colour alone */}
      {state === 'muted' && (
        <span className="absolute" style={{ width: 84, height: 2.5, background: 'rgba(255,255,255,0.85)', borderRadius: 2, transform: 'rotate(-45deg)', boxShadow: '0 0 6px rgba(0,0,0,0.4)' }} />
      )}
    </div>
  )
}
